import React, { Fragment } from "react";

import Above from "core-web/components/Breakpoints/Above";
import Below from "core-web/components/Breakpoints/Below";
import BleedWrapper from "theme/components/wrappers/BleedWrapper";
import CmsBlocks from "theme/libs/wordpress/content/CmsBlocks";
import Column from "theme/layouts/Column";
import Div from "theme/layouts/Div";
import Grid from "theme/layouts/Grid";
import PropTypes from "prop-types";
import ScrollableBox from "core-web/components/ScrollableBox";
import TextLink from "theme/components/text/TextLink";
import TextStyles from "theme/components/text/TextStyles";
import Waterfall from "theme/layouts/Waterfall";
import styled from "theme/libs/styled";
import transformHeading from "theme/libs/wordpress/utils/transformHeading";
import useBelowBreakpoint from "core-web/hooks/useBelowBreakpoint";
import variables from "theme/config/variables";

const HeaderWrapper = styled(Column)`
    grid-column: 1 / -1;
    gap: 22px 8px;

    ${({ theme }) => theme.above.tabletLg} {
        flex-direction: row;
        justify-content: space-between;
        align-items: end;
    }
`;

const StyledScrollableBox = styled(ScrollableBox)`
    display: flex;
    gap: 16px;
`;

const ThreeColumnsModule = ({ content }) => {
    const isMobile = useBelowBreakpoint("tabletLg");

    const { data, module_settings } = content;

    // We should not render the two column if the data is not correct.
    if (data?.columns?.length !== 3) {
        return null;
    }

    const numberOfColumns = data.columns?.length;

    // const reverseColumns = !!module_settings?.reverse_columns_in_mobile === "yes"; //TODO: implement reverseColumms
    const addModuleHeading = module_settings?.add_module_heading;
    const moduleHeader = transformHeading(data.heading.heading);

    const moduleLink = {
        text: data.heading?.link?.text || undefined,
        url: data.heading?.link?.url || undefined,
    };

    // mediaQuery is used by columns with a image background
    const mediaQuery = {
        w: [335, 535, 745, 495, 435, 500],
        q: 70,
    };

    return (
        <Waterfall as="section" my={["64px", null, null, null, "104px"]} rowGap="24px">
            {/* TODO: set margin as variable */}
            {addModuleHeading && (moduleHeader?.text || (moduleLink?.url && moduleLink.text)) && (
                <HeaderWrapper>
                    <>
                        {moduleHeader.text && (
                            <TextStyles
                                as={moduleHeader.type}
                                color={moduleHeader.color}
                                textStyles={moduleHeader.textStyle}
                                textWrap="balance"
                            >
                                {moduleHeader.text}
                            </TextStyles>
                        )}

                        {moduleLink.url && moduleLink.text && (
                            <TextLink
                                to={moduleLink.url}
                                withArrow={isMobile}
                                textStyles={["Myriad Pro/16-100", null, null, null, "Myriad Pro/18-100"]}
                                whiteSpace="nowrap"
                                color="#282828b3"
                            >
                                {moduleLink.text}
                            </TextLink>
                        )}
                    </>
                </HeaderWrapper>
            )}

            <Below
                breakpoint="tabletLg"
                render={() => (
                    <BleedWrapper>
                        <StyledScrollableBox scrollDirection="horizontal" px="20px" maxWidth="100vw">
                            {data.columns?.map((data, i) => (
                                <Fragment key={i}>
                                    <Div
                                        key={`${data.layout}${i}`}
                                        aspectRatio={4 / 6}
                                        flex="1 0 90%"
                                        maxHeight={`calc(100vh - ${variables.headerHeight}px)`}
                                    >
                                        <CmsBlocks
                                            data={data}
                                            numberOfColumns={numberOfColumns}
                                            mediaQuery={mediaQuery}
                                        />
                                    </Div>
                                </Fragment>
                            ))}
                        </StyledScrollableBox>
                    </BleedWrapper>
                )}
            />

            <Above
                breakpoint="tabletLg"
                render={() => (
                    <Grid columns={3}>
                        {data.columns?.map((data, i) => (
                            <Fragment key={i}>
                                <Div as="article" key={`${data.layout}${i}`} aspectRatio={4 / 6}>
                                    <CmsBlocks
                                        data={data}
                                        numberOfColumns={numberOfColumns}
                                        mediaQuery={mediaQuery}
                                        isThreeColumn
                                    />
                                </Div>
                            </Fragment>
                        ))}
                    </Grid>
                )}
            />
        </Waterfall>
    );
};

ThreeColumnsModule.propTypes = {
    content: PropTypes.shape({
        heading: PropTypes.object,
        columns: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
};

export default ThreeColumnsModule;
